class AATasksWorkspace {
  constructor(root) {
    this.root = root;
    this.teamMembers = [];
    this.clients = [];
    this.clientAssignments = {};
    this.tasks = [];
    this.packageServices = [];
    this.taskTypes = [];
    this.teamSummary = [];
    this.activeView = 'active';
    this.filterClientId = 'all';
    this.filterTaskType = 'all';
    this.filterAssigneeId = 'all';
    this.filterStatus = 'all';
    this.filterMonth = 'all';
    this.taskSearch = '';
    this.currentTeamMemberId = null;
    this.editorOpen = false;
    this.editingTaskId = null;

    this.bulkPasteOpen = false;
    this.bulkPasteText = '';
    this.bulkRows = [];

    this.statusOptions = [
      'Not Started',
      'In Progress',
      'In Waiting',
      'Waiting on Client',
      'Ready',
      'Completed'
    ];

    this.init();
  }

  escapeHtml(value = '') {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }
  setTaskUrl(taskId = null) {
    const url =
      new URL(window.location.href);

    if (taskId) {
      url.searchParams.set(
        'aa_task',
        String(taskId)
      );
    } else {
      url.searchParams.delete(
        'aa_task'
      );
    }

    window.history.replaceState(
      {},
      '',
      `${url.pathname}${url.search}${url.hash}`
    );
  }


  async init() {
    if (
      !window.AASupabaseClient?.supportsTasks
    ) {
      this.renderUnavailable();
      return;
    }

    const supported =
      await window.AASupabaseClient.supportsTasks();

    if (!supported) {
      this.renderUnavailable();
      return;
    }

    try {
      await this.loadData();

      const requestedTaskId =
        new URLSearchParams(
          window.location.search
        ).get('aa_task');

      const requestedTask =
        this.tasks.find(
          (task) =>
            String(task.id) ===
            String(
              requestedTaskId || ''
            )
        );

      if (requestedTask) {
        this.editingTaskId =
          requestedTask.id;

        this.editorOpen = true;

        this.activeView =
          String(
            requestedTask.status
          ) === 'Completed'
            ? 'completed'
            : 'active';
      }

      this.bindEvents();
      this.render();
    } catch (error) {
      console.error(
        'Unable to load central tasks:',
        error
      );

      this.root.innerHTML = `
        <div class="aa-card aa-headquarters__card">
          <p class="aa-card__label">Task System</p>
          <h3>Unable to load tasks</h3>
          <p>${this.escapeHtml(
            error?.message ||
            'The central task system could not be loaded.'
          )}</p>
        </div>
      `;
    }
  }

  renderUnavailable() {
    this.root.innerHTML = `
      <div class="aa-card aa-headquarters__card">
        <p class="aa-card__label">Task System</p>
        <h3>Task foundation is waiting on database setup</h3>
        <p>
          Headquarters is ready for the central task system,
          but the database migration has not been applied yet.
        </p>
      </div>
    `;
  }

  async loadData() {
    const auth =
      await window.AAWorkspaceContext
        ?.getWorkspaceAuthContext?.();

    const [
      teamMembers,
      clients,
      assignments,
      tasks,
      packageServices,
      taskTypes,
      teamSummary
    ] = await Promise.all([
      window.AASupabaseClient.getInternalTeamMembers(),
      window.AASupabaseClient.getClients(),
      window.AASupabaseClient.getClientPrimaryAssignments(),
      window.AASupabaseClient.getTasks(),
      window.AASupabaseClient
        .getTaskPackageServices?.() ||
        Promise.resolve([]),
      window.AASupabaseClient
        .getTaskTypes?.() ||
        Promise.resolve([]),
      window.AASupabaseClient
        .getTeamTaskSummary?.({
          month: this.getTeamPaceMonth()
        }) ||
        Promise.resolve([])
    ]);

    this.teamMembers =
      Array.isArray(teamMembers)
        ? teamMembers
        : [];

    this.clients =
      Array.isArray(clients)
        ? clients
        : [];

    this.clientAssignments =
      (assignments || []).reduce(
        (map, assignment) => {
          map[String(assignment.id)] =
            assignment.primary_team_member_id || '';
          return map;
        },
        {}
      );

    this.tasks =
      Array.isArray(tasks)
        ? tasks
        : [];

    this.packageServices =
      Array.isArray(packageServices)
        ? packageServices
        : [];

    this.taskTypes =
      Array.isArray(taskTypes)
        ? taskTypes
        : [];

    this.teamSummary =
      Array.isArray(teamSummary)
        ? teamSummary
        : [];

    const currentMember =
      this.teamMembers.find(
        (member) =>
          String(member.user_id) ===
          String(auth?.user?.id || '')
      );

    this.currentTeamMemberId =
      currentMember?.id || null;

    if (
      this.filterAssigneeId === 'all' &&
      this.currentTeamMemberId
    ) {
      this.filterAssigneeId =
        this.currentTeamMemberId;
    }
  }

  getClient(clientId) {
    return this.clients.find(
      (client) =>
        String(client.id) ===
        String(clientId)
    ) || null;
  }

  getTeamMember(teamMemberId) {
    return this.teamMembers.find(
      (member) =>
        String(member.id) ===
        String(teamMemberId)
    ) || null;
  }

  getTeamMemberName(teamMemberId) {
    const member =
      this.getTeamMember(teamMemberId);

    return (
      member?.display_name ||
      member?.aa_roles?.display_name ||
      'Unassigned'
    );
  }

  getClientName(clientId) {
    const client =
      this.getClient(clientId);

    return (
      client?.business_name ||
      'Internal'
    );
  }

  getPackageServicesForClient(
    clientId,
    dueDate = ''
  ) {
    if (!clientId) {
      return [];
    }

    const targetDate =
      dueDate ||
      [
        new Date().getFullYear(),
        String(
          new Date().getMonth() + 1
        ).padStart(2, '0'),
        String(
          new Date().getDate()
        ).padStart(2, '0')
      ].join('-');

    const eligible =
      this.packageServices
        .filter(
          (service) =>
            String(service.clientId) ===
              String(clientId) &&
            (
              !service.effectiveDate ||
              String(
                service.effectiveDate
              ) <= String(targetDate)
            ) &&
            (
              !service.expirationDate ||
              String(
                service.expirationDate
              ) >= String(targetDate)
            )
        )
        .sort(
          (a, b) =>
            String(
              b.effectiveDate || ''
            ).localeCompare(
              String(
                a.effectiveDate || ''
              )
            )
        );

    const seen =
      new Set();

    return eligible.filter(
      (service) => {
        const key =
          String(
            service.serviceCatalogId ||
            service.name
          );

        if (seen.has(key)) {
          return false;
        }

        seen.add(key);
        return true;
      }
    );
  }

  getPackageServiceName(
    packageServiceId
  ) {
    const service =
      this.packageServices.find(
        (item) =>
          String(item.id) ===
          String(
            packageServiceId || ''
          )
      );

    return service?.name || '';
  }

  getPackageServiceOptions(
    clientId,
    selectedId = '',
    dueDate = ''
  ) {
    const services =
      this.getPackageServicesForClient(
        clientId,
        dueDate
      );

    return [
      '<option value="">Not counted against package</option>',
      ...services.map(
        (service) => `
          <option
            value="${this.escapeHtml(service.id)}"
            ${String(selectedId) === String(service.id) ? 'selected' : ''}
          >
            ${this.escapeHtml(service.name)}
            ·
            ${this.escapeHtml(
              `${service.quantity} ${service.unit}`
            )}
          </option>
        `
      )
    ].join('');
  }

  getTaskScopeLabel(scope) {
    const labels = {
      client_deliverable:
        'Client Deliverable',
      internal_reminder:
        'Internal Reminder',
      internal_admin:
        'Internal / Admin'
    };

    return (
      labels[String(scope || '')] ||
      'Internal / Admin'
    );
  }

  resolvePackageServiceId(
    clientId,
    taskType,
    dueDate = ''
  ) {
    const services =
      this.getPackageServicesForClient(
        clientId,
        dueDate
      );

    if (!services.length) {
      return '';
    }

    const taskKey =
      this.normalizeLookup(taskType);

    if (!taskKey) {
      return '';
    }

    const direct =
      services.find((service) => {
        const serviceKey =
          this.normalizeLookup(
            service.name
          );

        return (
          serviceKey === taskKey ||
          serviceKey.includes(taskKey) ||
          taskKey.includes(serviceKey)
        );
      });

    if (direct) {
      return direct.id;
    }

    const aliasTests = [
      {
        task:
          taskKey.includes('email'),
        service:
          (value) =>
            value.includes('email')
      },
      {
        task:
          taskKey.includes('social'),
        service:
          (value) =>
            value.includes('social')
      },
      {
        task:
          taskKey.includes('banner'),
        service:
          (value) =>
            value.includes('websitebanner')
      },
      {
        task:
          taskKey.includes('catalog') ||
          taskKey.includes('flipbook'),
        service:
          (value) =>
            value.includes('catalog') ||
            value.includes('flipbook')
      },
      {
        task:
          taskKey.includes('manufacturer'),
        service:
          (value) =>
            value.includes('manufacturer')
      },
      {
        task:
          taskKey.includes('salespage'),
        service:
          (value) =>
            value.includes('salespage')
      },
      {
        task:
          taskKey.includes('websales') ||
          taskKey.includes('websitepromotion'),
        service:
          (value) =>
            value.includes('websales') ||
            value.includes('websitepromotion')
      }
    ];

    for (const test of aliasTests) {
      if (!test.task) {
        continue;
      }

      const match =
        services.find(
          (service) =>
            test.service(
              this.normalizeLookup(
                service.name
              )
            )
        );

      if (match) {
        return match.id;
      }
    }

    return '';
  }

  getTaskTypeOptions(
    selectedName = ''
  ) {
    const names = [
      ...new Set([
        ...this.taskTypes.map(
          (item) => item.name
        ),
        ...this.tasks.map(
          (task) => task.task_type
        ),
        selectedName
      ].filter(Boolean))
    ].sort((a, b) =>
      String(a).localeCompare(
        String(b),
        undefined,
        { sensitivity: 'base' }
      )
    );

    return [
      '<option value="">Choose task type</option>',
      ...names.map(
        (name) => `
          <option
            value="${this.escapeHtml(name)}"
            ${String(name) === String(selectedName) ? 'selected' : ''}
          >
            ${this.escapeHtml(name)}
          </option>
        `
      ),
      '<option value="__add__">+ Add Task Type</option>'
    ].join('');
  }

  getTaskTypeDefaultScope(name) {
    const match =
      this.taskTypes.find(
        (item) =>
          this.normalizeLookup(item.name) ===
          this.normalizeLookup(name)
      );

    return (
      match?.defaultScope ||
      'client_deliverable'
    );
  }

  getMonthlyAllowance(service) {
    if (!service) {
      return 0;
    }

    const quantity =
      Number(service.quantity || 0);

    const unit =
      String(service.unit || '')
        .toLowerCase();

    if (unit.includes('week')) {
      return quantity * 4;
    }

    return quantity;
  }

  getPackagePeriodKey(
    service,
    dueDate
  ) {
    const raw =
      dueDate ||
      new Date().toISOString()
        .slice(0, 10);

    const unit =
      String(service?.unit || '')
        .toLowerCase();

    if (unit.includes('year')) {
      return String(raw).slice(0, 4);
    }

    return String(raw).slice(0, 7);
  }

  getPackageUsage({
    packageServiceId,
    dueDate,
    excludeTaskId = null
  } = {}) {
    const service =
      this.packageServices.find(
        (item) =>
          String(item.id) ===
          String(packageServiceId || '')
      );

    if (!service) {
      return null;
    }

    const periodKey =
      this.getPackagePeriodKey(
        service,
        dueDate
      );

    const matching =
      this.tasks.filter(
        (task) => {
          if (
            String(task.id) ===
            String(excludeTaskId || '')
          ) {
            return false;
          }

          if (
            String(task.status || '') ===
            'Archived'
          ) {
            return false;
          }

          if (
            task.task_scope !==
              'client_deliverable' ||
            task.counts_toward_package !==
              true ||
            String(
              task.package_service_id || ''
            ) !==
              String(packageServiceId)
          ) {
            return false;
          }

          return (
            this.getPackagePeriodKey(
              service,
              task.due_date
            ) === periodKey
          );
        }
      );

    const scheduled =
      matching.reduce(
        (total, task) =>
          total +
          Number(
            task.package_units || 1
          ),
        0
      );

    const completed =
      matching
        .filter(
          (task) =>
            String(task.status) ===
            'Completed'
        )
        .reduce(
          (total, task) =>
            total +
            Number(
              task.package_units || 1
            ),
          0
        );

    return {
      service,
      allowance:
        this.getMonthlyAllowance(
          service
        ),
      scheduled,
      completed,
      periodKey
    };
  }

  renderPackageQuota({
    packageServiceId,
    dueDate,
    units = 1,
    status = 'Not Started',
    taskId = null
  } = {}) {
    const usage =
      this.getPackageUsage({
        packageServiceId,
        dueDate,
        excludeTaskId: taskId
      });

    if (!usage) {
      return '';
    }

    const projectedScheduled =
      usage.scheduled +
      Number(units || 1);

    const projectedCompleted =
      usage.completed +
      (
        String(status) === 'Completed'
          ? Number(units || 1)
          : 0
      );

    const remainingToSchedule =
      Math.max(
        usage.allowance -
        projectedScheduled,
        0
      );

    const remainingToComplete =
      Math.max(
        usage.allowance -
        projectedCompleted,
        0
      );

    const overBy =
      Math.max(
        projectedScheduled -
        usage.allowance,
        0
      );

    return `
      <div
        class="aa-task-package-quota
          ${overBy > 0 ? 'is-over' : ''}"
        data-aa-task-package-quota
      >
        <div>
          <strong>
            ${this.escapeHtml(
              usage.service.name
            )}
          </strong>
          <span>
            ${this.escapeHtml(
              usage.periodKey
            )}
          </span>
        </div>

        <div class="aa-task-package-quota__stats">
          <span>
            <b>${usage.allowance}</b>
            contracted
          </span>
          <span>
            <b>${projectedScheduled}</b>
            scheduled
          </span>
          <span>
            <b>${projectedCompleted}</b>
            completed
          </span>
          <span>
            <b>${remainingToSchedule}</b>
            unscheduled
          </span>
          <span>
            <b>${remainingToComplete}</b>
            still to complete
          </span>
        </div>

        ${overBy > 0 ? `
          <p>
            Package allowance exceeded by
            <strong>${overBy}</strong>.
            You can still save this task.
          </p>
        ` : ''}
      </div>
    `;
  }

  syncTaskEditor(
    form,
    {
      autoMap = false
    } = {}
  ) {
    if (!form) {
      return;
    }

    const getField = (name) =>
      form.querySelector(
        `[data-aa-task-field="${name}"]`
      );

    const clientId =
      getField('client_id')?.value || '';

    const taskType =
      getField('task_type')?.value || '';

    const scopeField =
      getField('task_scope');

    const packageField =
      getField('package_service_id');

    const unitsField =
      getField('package_units');

    const dueField =
      getField('due_date');

    const statusField =
      getField('status');

    if (packageField && clientId) {
      const currentSelected =
        packageField.value || '';

      const validServices =
        this.getPackageServicesForClient(
          clientId,
          dueField?.value || ''
        );

      const stillValid =
        validServices.some(
          (service) =>
            String(service.id) ===
            String(currentSelected)
        );

      packageField.innerHTML =
        this.getPackageServiceOptions(
          clientId,
          stillValid
            ? currentSelected
            : '',
          dueField?.value || ''
        );
    }

    if (
      autoMap &&
      taskType &&
      taskType !== '__add__'
    ) {
      const defaultScope =
        this.getTaskTypeDefaultScope(
          taskType
        );

      if (scopeField) {
        scopeField.value =
          clientId
            ? defaultScope
            : 'internal_admin';
      }

      if (
        packageField &&
        scopeField?.value ===
          'client_deliverable'
      ) {
        const matched =
          this.resolvePackageServiceId(
            clientId,
            taskType,
            dueField?.value || ''
          );

        if (matched) {
          packageField.value =
            matched;
        }
      }
    }

    const deliverable =
      scopeField?.value ===
      'client_deliverable';

    form
      .querySelectorAll(
        '[data-aa-task-package-field]'
      )
      .forEach((element) => {
        element.hidden =
          !deliverable;
      });

    if (!deliverable) {
      if (packageField) {
        packageField.value = '';
      }

      const quota =
        form.querySelector(
          '[data-aa-task-package-quota-wrap]'
        );

      if (quota) {
        quota.innerHTML = '';
      }

      return;
    }

    const quota =
      form.querySelector(
        '[data-aa-task-package-quota-wrap]'
      );

    if (quota) {
      quota.innerHTML =
        packageField?.value
          ? this.renderPackageQuota({
              packageServiceId:
                packageField.value,
              dueDate:
                dueField?.value || '',
              units:
                Number(
                  unitsField?.value || 1
                ),
              status:
                statusField?.value ||
                'Not Started',
              taskId:
                this.editingTaskId
            })
          : '';
    }
  }

  async saveNewTaskType(input) {
    if (
      !input ||
      input.dataset.saving === 'true'
    ) {
      return;
    }

    const name =
      String(input.value || '').trim();

    if (!name) {
      return;
    }

    input.dataset.saving = 'true';

    const form =
      input.closest(
        '[data-aa-task-form]'
      );

    const scope =
      form?.querySelector(
        '[data-aa-task-field="task_scope"]'
      )?.value ||
      'client_deliverable';

    try {
      await window.AASupabaseClient
        .addTaskType({
          name,
          defaultScope: scope
        });

      this.taskTypes =
        await window.AASupabaseClient
          .getTaskTypes();

      const select =
        form?.querySelector(
          '[data-aa-task-field="task_type"]'
        );

      if (select) {
        select.innerHTML =
          this.getTaskTypeOptions(
            name
          );

        select.value = name;
      }

      input.value = '';
      input.hidden = true;
      delete input.dataset.saving;

      this.syncTaskEditor(
        form,
        { autoMap: true }
      );
    } catch (error) {
      console.error(
        'Unable to add task type:',
        error
      );

      delete input.dataset.saving;

      this.showFeedback(
        'Unable to add that task type.'
      );
    }
  }

  normalizeLookup(value = '') {
    return String(value)
      .trim()
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '');
  }

  getClientAliasMap() {
    const aliases = new Map();

    this.clients.forEach((client) => {
      const businessName =
        String(
          client.business_name || ''
        ).trim();

      const clientCode =
        String(
          client.client_code || ''
        ).trim();

      if (clientCode) {
        aliases.set(
          this.normalizeLookup(
            clientCode
          ),
          client.id
        );
      }

      if (businessName) {
        aliases.set(
          this.normalizeLookup(
            businessName
          ),
          client.id
        );
      }
    });

    return aliases;
  }

  resolveClientId(value = '') {
    const raw =
      String(value || '').trim();

    if (!raw) {
      return '';
    }

    return (
      this.getClientAliasMap().get(
        this.normalizeLookup(raw)
      ) || ''
    );
  }

  resolveAssigneeId(value = '') {
    const raw =
      this.normalizeLookup(value);

    if (!raw) {
      return '';
    }

    const member =
      this.teamMembers.find(
        (item) =>
          this.normalizeLookup(
            item.display_name ||
            item.aa_roles?.display_name ||
            ''
          ) === raw
      );

    return member?.id || '';
  }

  normalizeBulkStatus(value = '') {
    const raw =
      String(value || '').trim();

    if (!raw) {
      return 'Not Started';
    }

    const normalized =
      this.statusOptions.find(
        (status) =>
          this.normalizeLookup(status) ===
          this.normalizeLookup(raw)
      );

    return normalized || 'Not Started';
  }

  normalizeBulkDate(value = '') {
    const raw =
      String(value || '').trim();

    if (!raw) {
      return '';
    }

    if (
      /^\d{4}-\d{2}-\d{2}$/.test(raw)
    ) {
      return raw;
    }

    const slashMatch =
      raw.match(
        /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/
      );

    if (slashMatch) {
      let year =
        Number(slashMatch[3]);

      if (year < 100) {
        year += 2000;
      }

      const month =
        String(
          Number(slashMatch[1])
        ).padStart(2, '0');

      const day =
        String(
          Number(slashMatch[2])
        ).padStart(2, '0');

      return `${year}-${month}-${day}`;
    }

    return '';
  }

  isBulkHeaderRow(columns = []) {
    const combined =
      columns
        .join(' ')
        .toLowerCase();

    return (
      combined.includes('task type') &&
      (
        combined.includes('specific') ||
        combined.includes('assigned')
      )
    );
  }

  isDuplicateBulkRow(row) {
    return this.tasks.some(
      (task) =>
        String(task.status || '') !==
          'Archived' &&
        String(task.client_id || '') ===
          String(row.clientId || '') &&
        this.normalizeLookup(
          task.specific_item
        ) ===
          this.normalizeLookup(
            row.specificItem
          ) &&
        String(task.due_date || '') ===
          String(row.dueDate || '')
    );
  }

  parseBulkPaste(text = '') {
    const rows = [];

    String(text || '')
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .split('\n')
      .forEach((line, index) => {
        if (!String(line).trim()) {
          return;
        }

        const columns =
          String(line).split('\t');

        if (this.isBulkHeaderRow(columns)) {
          return;
        }

        const hasBlankColumnA =
          String(columns[0] || '').trim() === '';

        const workingColumns =
          hasBlankColumnA
            ? columns.slice(1)
            : [...columns];

        if (workingColumns.length < 2) {
          rows.push({
            rowNumber: index + 1,
            include: false,
            clientCode: '',
            clientId: '',
            taskType: '',
            specificItem:
              String(line).trim(),
            assigneeId: '',
            status: 'Not Started',
            dueDate: '',
            notes: '',
            warnings: [
              'This row does not have enough spreadsheet columns.'
            ]
          });

          return;
        }

        while (workingColumns.length < 7) {
          workingColumns.push('');
        }

        const cells = [
          workingColumns[0],
          workingColumns[1],
          workingColumns[2],
          workingColumns[3],
          workingColumns[4],
          workingColumns[5],
          workingColumns.slice(6).join('\t')
        ];

        const [
          clientCode,
          taskType,
          specificItem,
          assignee,
          status,
          dueDateRaw,
          notes
        ] = cells.map(
          (value) =>
            String(value || '').trim()
        );

        const clientId =
          this.resolveClientId(
            clientCode
          );

        const assigneeId =
          this.resolveAssigneeId(
            assignee
          ) ||
          (
            clientId
              ? this.clientAssignments[
                  String(clientId)
                ]
              : ''
          ) ||
          this.currentTeamMemberId ||
          '';

        const dueDate =
          this.normalizeBulkDate(
            dueDateRaw
          );

        const warnings = [];

        if (clientCode && !clientId) {
          warnings.push(
            `Client "${clientCode}" needs to be matched.`
          );
        }

        if (!taskType) {
          warnings.push(
            'Task Type is required.'
          );
        }

        if (!specificItem) {
          warnings.push(
            'Specific Item is required.'
          );
        }

        if (
          dueDateRaw &&
          !dueDate
        ) {
          warnings.push(
            `Date "${dueDateRaw}" needs to be corrected.`
          );
        }

        const row = {
          rowNumber: index + 1,
          include: !clientCode || Boolean(clientId),
          clientCode,
          clientId,
          taskType,
          specificItem,
          assigneeId,
          status:
            this.normalizeBulkStatus(
              status
            ),
          dueDate,
          notes,
          warnings
        };

        if (
          this.isDuplicateBulkRow(row)
        ) {
          row.include = false;
          row.warnings.push(
            'Possible duplicate of an existing task.'
          );
        }

        rows.push(row);
      });

    return rows;
  }

  getTeamPaceMonth() {
    if (
      this.filterMonth !== 'all' &&
      /^\d{4}-\d{2}$/.test(
        String(this.filterMonth)
      )
    ) {
      return this.filterMonth;
    }

    const now = new Date();

    return [
      now.getFullYear(),
      String(
        now.getMonth() + 1
      ).padStart(2, '0')
    ].join('-');
  }

  formatTeamPaceMonth() {
    const [year, month] =
      this.getTeamPaceMonth()
        .split('-')
        .map(Number);

    return new Intl.DateTimeFormat(
      'en-US',
      {
        month: 'long',
        year: 'numeric'
      }
    ).format(
      new Date(
        year,
        month - 1,
        1
      )
    );
  }

  async refreshTeamSummary() {
    if (
      !window.AASupabaseClient
        ?.getTeamTaskSummary
    ) {
      this.teamSummary = [];
      return;
    }

    try {
      const summary =
        await window.AASupabaseClient
          .getTeamTaskSummary({
            month:
              this.getTeamPaceMonth()
          });

      this.teamSummary =
        Array.isArray(summary)
          ? summary
          : [];
    } catch (error) {
      console.error(
        'Unable to refresh team pace:',
        error
      );

      this.teamSummary = [];
    }
  }

  renderTeamPace() {
    if (!this.teamSummary.length) {
      return '';
    }

    const members =
      [...this.teamSummary]
        .sort(
          (a, b) =>
            String(
              a.teamMemberName
            ).localeCompare(
              String(
                b.teamMemberName
              )
            )
        );

    return `
      <section class="aa-team-workload">
        <div class="aa-team-workload__header">
          <div>
            <p class="aa-card__label">
              Team Workload
            </p>

            <h3>
              ${this.escapeHtml(
                this.formatTeamPaceMonth()
              )}
            </h3>
          </div>

          <span>
            Assignment balance + monthly completion
          </span>
        </div>

        <div class="aa-team-workload__grid">
          ${members.map(
            (member) => `
              <article class="aa-team-workload__member">
                <div class="aa-team-workload__member-head">
                  <div>
                    <strong>
                      ${this.escapeHtml(
                        member.teamMemberName
                      )}
                    </strong>

                    <small>
                      ${member.clientsOwned}
                      client${member.clientsOwned === 1 ? '' : 's'}
                      ·
                      ${member.total}
                      ${this.escapeHtml(
                        this.formatTeamPaceMonth()
                      )} task${member.total === 1 ? '' : 's'}
                    </small>
                  </div>

                  <b>
                    ${member.completionPercent}%
                  </b>
                </div>

                <div class="aa-team-workload__bar">
                  <span
                    style="width: ${Math.max(
                      0,
                      Math.min(
                        Number(
                          member.completionPercent
                        ),
                        100
                      )
                    )}%"
                  ></span>
                </div>

                <div class="aa-team-workload__statuses">
                  <div>
                    <span>Not Started</span>
                    <strong>${member.notStarted}</strong>
                  </div>

                  <div>
                    <span>In Progress</span>
                    <strong>${member.inProgress}</strong>
                  </div>

                  <div>
                    <span>Waiting</span>
                    <strong>${member.waiting}</strong>
                  </div>

                  <div class="${member.issue > 0 ? 'is-blocked' : ''}">
                    <span>Issue</span>
                    <strong>${member.issue}</strong>
                  </div>

                  <div class="aa-team-workload__ready">
                    <span>Ready</span>
                    <strong>${this.tasks.filter(task =>
                      String(task.assignee_team_member_id || '') === String(member.teamMemberId) &&
                      String(task.due_date || '').slice(0, 7) === this.getTeamPaceMonth() &&
                      String(task.status || '') === 'Ready'
                    ).length}</strong>
                  </div>

                  <div>
                    <span>Completed</span>
                    <strong>${member.completed}</strong>
                  </div>

                  <div class="${member.overdue > 0 ? 'is-overdue' : ''}">
                    <span>Overdue</span>
                    <strong>${member.overdue}</strong>
                  </div>

                  <div class="aa-team-workload__total">
                    <span>Total Assigned</span>
                    <strong>${member.total}</strong>
                  </div>
                </div>
              </article>
            `
          ).join('')}
        </div>
      </section>
    `;
  }

  getVisibleTasks() {
    let tasks =
      this.tasks.filter(
        (task) =>
          String(task.status || '') !==
          'Archived'
      );

    tasks =
      tasks.filter(
        (task) =>
          this.activeView ===
            'completed'
            ? String(task.status) ===
              'Completed'
            : String(task.status) !==
              'Completed'
      );

    if (
      this.filterClientId !== 'all'
    ) {
      tasks =
        tasks.filter(
          (task) =>
            String(
              task.client_id || ''
            ) ===
            String(
              this.filterClientId
            )
        );
    }

    if (
      this.filterTaskType !== 'all'
    ) {
      tasks =
        tasks.filter(
          (task) =>
            String(task.task_type) ===
            String(
              this.filterTaskType
            )
        );
    }

    if (
      this.filterAssigneeId !== 'all'
    ) {
      tasks =
        tasks.filter(
          (task) =>
            String(
              task.assignee_team_member_id ||
              ''
            ) ===
            String(
              this.filterAssigneeId
            )
        );
    }

    if (
      this.filterStatus !== 'all'
    ) {
      tasks =
        tasks.filter(
          (task) =>
            String(task.status) ===
            String(
              this.filterStatus
            )
        );
    }

    if (
      this.filterMonth !== 'all'
    ) {
      tasks =
        tasks.filter(
          (task) =>
            String(
              task.due_date || ''
            ).slice(0, 7) ===
            String(
              this.filterMonth
            )
        );
    }

    const search =
      String(this.taskSearch || '')
        .trim()
        .toLowerCase();

    if (search) {
      tasks =
        tasks.filter(
          (task) =>
            [
              this.getClientName(
                task.client_id
              ),
              task.task_type,
              task.specific_item,
              task.notes,
              this.getTeamMemberName(
                task.assignee_team_member_id
              )
            ]
              .filter(Boolean)
              .join(' ')
              .toLowerCase()
              .includes(search)
        );
    }

    if (
      this.activeView === 'completed'
    ) {
      tasks.sort(
        (a, b) =>
          String(
            b.completed_at ||
            b.updated_at ||
            ''
          ).localeCompare(
            String(
              a.completed_at ||
              a.updated_at ||
              ''
            )
          )
      );
    } else {
      tasks.sort(
        (a, b) => {
          const aDate =
            a.due_date ||
            '9999-12-31';

          const bDate =
            b.due_date ||
            '9999-12-31';

          return (
            String(aDate)
              .localeCompare(
                String(bDate)
              ) ||
            String(a.created_at || '')
              .localeCompare(
                String(b.created_at || '')
              )
          );
        }
      );
    }

    return tasks;
  }

  getTaskFilterMonths() {
    return [
      ...new Set(
        this.tasks
          .map(
            (task) =>
              String(
                task.due_date || ''
              ).slice(0, 7)
          )
          .filter(
            (value) =>
              /^\d{4}-\d{2}$/.test(
                value
              )
          )
      )
    ].sort(
      (a, b) =>
        String(b).localeCompare(
          String(a)
        )
    );
  }

  renderTaskFilters() {
    const taskTypes = [
      ...new Set([
        ...this.taskTypes.map(
          (item) => item.name
        ),
        ...this.tasks.map(
          (task) => task.task_type
        )
      ].filter(Boolean))
    ].sort((a, b) =>
      String(a).localeCompare(
        String(b),
        undefined,
        { sensitivity: 'base' }
      )
    );

    return `
      <div class="aa-task-filters">
        <label>
          <span>Client</span>
          <select
            data-aa-task-list-filter="client"
          >
            <option value="all">
              All Clients
            </option>
            ${this.clients.map(
              (client) => `
                <option
                  value="${this.escapeHtml(client.id)}"
                  ${String(this.filterClientId) === String(client.id) ? 'selected' : ''}
                >
                  ${this.escapeHtml(client.business_name)}
                </option>
              `
            ).join('')}
          </select>
        </label>

        <label>
          <span>Task Type</span>
          <select
            data-aa-task-list-filter="task_type"
          >
            <option value="all">
              All Task Types
            </option>
            ${taskTypes.map(
              (name) => `
                <option
                  value="${this.escapeHtml(name)}"
                  ${String(this.filterTaskType) === String(name) ? 'selected' : ''}
                >
                  ${this.escapeHtml(name)}
                </option>
              `
            ).join('')}
          </select>
        </label>

        <label>
          <span>Assigned To</span>
          <select
            data-aa-task-list-filter="assignee"
          >
            <option value="all">
              Everyone
            </option>
            ${this.teamMembers.map(
              (member) => `
                <option
                  value="${this.escapeHtml(member.id)}"
                  ${String(this.filterAssigneeId) === String(member.id) ? 'selected' : ''}
                >
                  ${this.escapeHtml(
                    member.display_name ||
                    member.aa_roles?.display_name ||
                    'Team Member'
                  )}
                </option>
              `
            ).join('')}
          </select>
        </label>

        <label>
          <span>Status</span>
          <select
            data-aa-task-list-filter="status"
          >
            <option value="all">
              All Statuses
            </option>
            ${this.statusOptions.map(
              (status) => `
                <option
                  value="${this.escapeHtml(status)}"
                  ${String(this.filterStatus) === String(status) ? 'selected' : ''}
                >
                  ${this.escapeHtml(status)}
                </option>
              `
            ).join('')}
          </select>
        </label>

        <label>
          <span>Month</span>
          <select
            data-aa-task-list-filter="month"
          >
            <option value="all">
              All Dates
            </option>
            ${this.getTaskFilterMonths()
              .map((value) => {
                const [year, month] =
                  value.split('-')
                    .map(Number);

                const label =
                  new Intl.DateTimeFormat(
                    'en-US',
                    {
                      month: 'long',
                      year: 'numeric'
                    }
                  ).format(
                    new Date(
                      year,
                      month - 1,
                      1
                    )
                  );

                return `
                  <option
                    value="${this.escapeHtml(value)}"
                    ${String(this.filterMonth) === String(value) ? 'selected' : ''}
                  >
                    ${this.escapeHtml(label)}
                  </option>
                `;
              })
              .join('')}
          </select>
        </label>

        <label class="aa-task-filters__search">
          <span>Search</span>
          <input
            type="search"
            placeholder="Specific item, notes..."
            value="${this.escapeHtml(this.taskSearch)}"
            data-aa-task-search
          >
        </label>
      </div>
    `;
  }

  formatDate(value) {
    if (!value) {
      return '—';
    }

    const date =
      new Date(`${value}T12:00:00`);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat(
      'en-US',
      {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }
    ).format(date);
  }

  getStatusOptions(selectedStatus) {
    const values =
      this.statusOptions.includes(selectedStatus)
        ? this.statusOptions
        : [
            ...this.statusOptions,
            selectedStatus
          ].filter(Boolean);

    return values
      .map(
        (status) => `
          <option
            value="${this.escapeHtml(status)}"
            ${status === selectedStatus ? 'selected' : ''}
          >
            ${this.escapeHtml(status)}
          </option>
        `
      )
      .join('');
  }

  getAssigneeOptions(selectedId) {
    return [
      `
        <option value="" ${selectedId ? '' : 'selected'}>
          Unassigned
        </option>
      `,
      ...this.teamMembers.map(
        (member) => `
          <option
            value="${this.escapeHtml(member.id)}"
            ${String(selectedId || '') === String(member.id) ? 'selected' : ''}
          >
            ${this.escapeHtml(
              member.display_name ||
              member.aa_roles?.display_name ||
              'Team Member'
            )}
          </option>
        `
      )
    ].join('');
  }

  render() {
    const visibleTasks =
      this.getVisibleTasks();

    const activeTasks =
      this.tasks.filter(
        (task) =>
          ![
            'Archived',
            'Completed'
          ].includes(
            String(task.status || '')
          )
      );

    const completedTasks =
      this.tasks.filter(
        (task) =>
          String(task.status) ===
          'Completed'
      );

    const teamSummary =
      this.teamMembers
        .map((member) => {
          const count =
            activeTasks.filter(
              (task) =>
                String(
                  task.assignee_team_member_id ||
                  ''
                ) ===
                String(member.id)
            ).length;

          return `${this.escapeHtml(
            member.display_name ||
            member.aa_roles?.display_name ||
            'Team'
          )} ${count}`;
        })
        .join(' · ');

    this.root.innerHTML = `
      <section class="aa-task-workspace">
        ${this.renderTeamPace()}

        <div class="aa-task-toolbar aa-task-toolbar--compact">
          <div>
            <strong>
              ${activeTasks.length}
              active task${activeTasks.length === 1 ? '' : 's'}
            </strong>
            ${teamSummary
              ? `<span> · ${teamSummary}</span>`
              : ''
            }
          </div>

          <div class="aa-task-toolbar__actions">
            <button
              type="button"
              class="aa-button"
              data-aa-task-bulk-open
            >
              Paste Tasks
            </button>

            <button
              type="button"
              class="aa-button aa-button--primary"
              data-aa-task-add
            >
              + Add Task
            </button>
          </div>
        </div>

        <div
          class="aa-task-view-tabs"
          role="tablist"
          aria-label="Task view"
        >
          <button
            type="button"
            class="aa-task-view-tab
              ${this.activeView === 'active' ? 'is-active' : ''}"
            data-aa-task-view="active"
            role="tab"
            aria-selected="${this.activeView === 'active'}"
          >
            Active
            <small>${activeTasks.length}</small>
          </button>

          <button
            type="button"
            class="aa-task-view-tab
              ${this.activeView === 'completed' ? 'is-active' : ''}"
            data-aa-task-view="completed"
            role="tab"
            aria-selected="${this.activeView === 'completed'}"
          >
            Completed
            <small>${completedTasks.length}</small>
          </button>
        </div>

        ${this.renderTaskFilters()}

        ${this.bulkPasteOpen
          ? this.renderBulkPaste()
          : ''
        }

        ${this.editorOpen
          ? this.renderEditor()
          : ''
        }

        <div
          class="aa-task-feedback"
          data-aa-task-feedback
          hidden
        ></div>

        <div class="aa-task-results-meta">
          <span>
            ${visibleTasks.length}
            result${visibleTasks.length === 1 ? '' : 's'}
          </span>
        </div>

        <div class="aa-task-table-wrap">
          ${visibleTasks.length
            ? this.renderTaskTable(visibleTasks)
            : this.renderEmptyState()
          }
        </div>
      </section>
    `;

    if (this.editorOpen) {
      requestAnimationFrame(
        () => {
          const form =
            this.root.querySelector(
              '[data-aa-task-form]'
            );

          this.syncTaskEditor(form);
        }
      );
    }
  }

  renderTab(value, label, count) {
    const active =
      String(this.activeAssigneeFilter) ===
      String(value);

    return `
      <button
        type="button"
        class="aa-task-tab ${active ? 'is-active' : ''}"
        data-aa-task-filter="${this.escapeHtml(value)}"
        role="tab"
        aria-selected="${active ? 'true' : 'false'}"
      >
        <span>${this.escapeHtml(label)}</span>
        <small>${Number(count || 0)}</small>
      </button>
    `;
  }

  renderBulkPaste() {
    return `
      <section class="aa-task-bulk">
        <div class="aa-task-editor__header">
          <div>
            <p class="aa-card__label">
              Bulk Paste
            </p>
            <h3>Paste Spreadsheet Tasks</h3>
            <p>
              Paste columns B–H, or A–H with column A blank.
              We will review everything before anything is added.
            </p>
          </div>

          <button
            type="button"
            class="aa-button"
            data-aa-task-bulk-close
          >
            Close
          </button>
        </div>

        <form data-aa-task-bulk-parse>
          <label class="aa-task-bulk__paste-field">
            Spreadsheet rows
            <textarea
              data-aa-task-bulk-text
              rows="8"
              placeholder="FW&#9;Website Banner&#9;20% Metallic Fabrics&#9;Tiffany&#9;Not Started&#9;10/08/2026&#9;Launch ahead of sale"
            >${this.escapeHtml(
              this.bulkPasteText
            )}</textarea>
          </label>

          <div class="aa-task-editor__actions">
            <span class="aa-task-bulk__hint">
              B Client · C Task Type · D Specific Item ·
              E Assignee · F Status · G Date · H Notes
            </span>

            <button
              type="button"
              class="aa-button aa-button--primary"
              data-aa-task-bulk-review
            >
              Review Tasks
            </button>
          </div>
        </form>

        ${this.bulkRows.length
          ? this.renderBulkReview()
          : ''
        }
      </section>
    `;
  }

  renderBulkClientOptions(selectedId) {
    return [
      `
        <option value="" ${selectedId ? '' : 'selected'}>
          Select client
        </option>
      `,
      ...this.clients.map(
        (client) => `
          <option
            value="${this.escapeHtml(client.id)}"
            ${String(selectedId || '') === String(client.id) ? 'selected' : ''}
          >
            ${this.escapeHtml(client.business_name)}
          </option>
        `
      )
    ].join('');
  }

  renderBulkReview() {
    const readyCount =
      this.bulkRows.filter(
        (row) =>
          row.include &&
          row.clientId &&
          row.taskType &&
          row.specificItem &&
          !row.warnings.length
      ).length;

    const attentionCount =
      this.bulkRows.filter(
        (row) =>
          row.warnings.length
      ).length;

    return `
      <form
        class="aa-task-bulk__review"
        data-aa-task-bulk-import
      >
        <div class="aa-task-bulk__review-header">
          <div>
            <p class="aa-card__label">
              Review Before Import
            </p>
            <h3>
              ${this.bulkRows.length}
              task${this.bulkRows.length === 1 ? '' : 's'} parsed
            </h3>
            <p class="aa-task-bulk__summary">
              ${readyCount} ready
              ${attentionCount ? ` · ${attentionCount} need attention` : ''}
            </p>
          </div>

          <div class="aa-task-bulk__review-actions">
            <button
              type="button"
              class="aa-button"
              data-aa-task-bulk-select-all
            >
              Select All
            </button>

            <button
              type="button"
              class="aa-button"
              data-aa-task-bulk-clear-all
            >
              Clear
            </button>

          </div>
        </div>

        <div class="aa-task-bulk-table-wrap">
          <div class="aa-task-bulk-table">
            <div class="aa-task-bulk-grid aa-task-bulk-grid--header">
              <span></span>
              <span>Client</span>
              <span>Task Type</span>
              <span>Specific Item</span>
              <span>Assigned To</span>
              <span>Status</span>
              <span>Due</span>
              <span>Notes</span>
            </div>

            ${this.bulkRows.map(
              (row, index) => `
                <div
                  class="aa-task-bulk-grid aa-task-bulk-grid--row ${row.warnings.length ? 'has-warning' : ''}"
                  data-aa-bulk-row="${index}"
                >
                  <label class="aa-task-bulk-grid__check" title="Include row">
                    <input
                      type="checkbox"
                      data-aa-bulk-field="include"
                      ${row.include ? 'checked' : ''}
                    >
                    <span>${row.rowNumber}</span>
                  </label>

                  <div>
                    <select
                      data-aa-bulk-field="client_id"
                      aria-label="Client"
                    >
                      ${this.renderBulkClientOptions(
                        row.clientId
                      )}
                    </select>
                    ${row.clientCode ? `
                      <small>
                        ${this.escapeHtml(row.clientCode)}
                      </small>
                    ` : ''}
                  </div>

                  <input
                    type="text"
                    data-aa-bulk-field="task_type"
                    aria-label="Task Type"
                    value="${this.escapeHtml(row.taskType)}"
                  >

                  <input
                    type="text"
                    data-aa-bulk-field="specific_item"
                    aria-label="Specific Item"
                    value="${this.escapeHtml(row.specificItem)}"
                  >

                  <select
                    data-aa-bulk-field="assignee_team_member_id"
                    aria-label="Assigned To"
                  >
                    ${this.getAssigneeOptions(
                      row.assigneeId
                    )}
                  </select>

                  <select
                    data-aa-bulk-field="status"
                    aria-label="Status"
                  >
                    ${this.getStatusOptions(
                      row.status
                    )}
                  </select>

                  <input
                    type="date"
                    data-aa-bulk-field="due_date"
                    aria-label="Due Date"
                    value="${this.escapeHtml(row.dueDate)}"
                  >

                  <input
                    type="text"
                    data-aa-bulk-field="notes"
                    aria-label="Notes / Details"
                    value="${this.escapeHtml(row.notes)}"
                  >

                  ${row.warnings.length ? `
                    <div class="aa-task-bulk-grid__warning">
                      ${row.warnings.map(
                        (warning) => `
                          <span>
                            ${this.escapeHtml(warning)}
                          </span>
                        `
                      ).join('')}
                    </div>
                  ` : ''}
                </div>
              `
            ).join('')}
          </div>
        </div>

        <div class="aa-task-bulk__footer">
          <span>
            Review the rows above, then add only the selected tasks.
          </span>

          <button
            type="submit"
            class="aa-button aa-button--primary"
          >
            Add Selected Tasks
          </button>
        </div>
      </form>
    `;
  }

  getOriginLabel(task) {
    if (!task) {
      return 'Manual';
    }

    if (
      String(task.source_type || '') ===
      'email_request'
    ) {
      return 'Client Email Request';
    }

    if (
      String(task.source_type || '') ===
      'playbook_reopen_request'
    ) {
      return 'Client Playbook Reopen';
    }

    const labels = {
      manual: 'Manual',
      bulk_import: 'Bulk Paste',
      recurring: 'Recurring',
      client_request: 'Client Request',
      system: 'System'
    };

    return (
      labels[String(task.origin || '')] ||
      String(task.origin || 'Manual')
    );
  }

  renderEditor() {
    const task =
      this.tasks.find(
        (item) =>
          String(item.id) ===
          String(this.editingTaskId)
      ) || null;

    const selectedClientId =
      task?.client_id || '';

    const selectedTaskType =
      task?.task_type || '';

    const selectedScope =
      task?.task_scope ||
      (
        selectedClientId
          ? this.getTaskTypeDefaultScope(
              selectedTaskType
            )
          : 'internal_admin'
      );

    const defaultAssignee =
      task?.assignee_team_member_id ||
      (
        selectedClientId
          ? this.clientAssignments[
              String(selectedClientId)
            ]
          : this.currentTeamMemberId
      ) ||
      '';

    const selectedPackageService =
      task?.package_service_id ||
      (
        selectedScope ===
          'client_deliverable'
          ? this.resolvePackageServiceId(
              selectedClientId,
              selectedTaskType,
              task?.due_date || ''
            )
          : ''
      );

    return `
      <div
        class="aa-task-editor-overlay"
        data-aa-task-editor-overlay
      >
        <form
          class="aa-task-editor aa-task-editor--drawer"
          data-aa-task-form
        >
          <div class="aa-task-editor__header">
            <div>
              <p class="aa-card__label">
                ${task ? 'Edit Task' : 'New Task'}
              </p>

              <h3>
                ${task
                  ? this.escapeHtml(task.specific_item)
                  : 'Add Task'
                }
              </h3>

              ${task ? `
                <span class="aa-task-editor__source">
                  Source:
                  ${this.escapeHtml(
                    this.getOriginLabel(task)
                  )}
                </span>
              ` : ''}
            </div>

            <button
              type="button"
              class="aa-task-editor__close"
              data-aa-task-cancel
              aria-label="Close task editor"
            >
              ×
            </button>
          </div>

          <div class="aa-task-editor__grid">
            <label>
              Client
              <select data-aa-task-field="client_id">
                <option value="">
                  Internal / No Client
                </option>
                ${this.clients.map(
                  (client) => `
                    <option
                      value="${this.escapeHtml(client.id)}"
                      ${String(selectedClientId) === String(client.id) ? 'selected' : ''}
                    >
                      ${this.escapeHtml(client.business_name)}
                    </option>
                  `
                ).join('')}
              </select>
            </label>

            <label>
              Task Type
              <select
                data-aa-task-field="task_type"
                required
              >
                ${this.getTaskTypeOptions(
                  selectedTaskType
                )}
              </select>

              <input
                type="text"
                class="aa-task-type-new"
                placeholder="New task type"
                data-aa-new-task-type
                hidden
              >
            </label>

            <label>
              Work Type
              <select
                data-aa-task-field="task_scope"
              >
                <option
                  value="client_deliverable"
                  ${selectedScope === 'client_deliverable' ? 'selected' : ''}
                >
                  Client Deliverable
                </option>
                <option
                  value="internal_reminder"
                  ${selectedScope === 'internal_reminder' ? 'selected' : ''}
                >
                  Internal Reminder
                </option>
                <option
                  value="internal_admin"
                  ${selectedScope === 'internal_admin' ? 'selected' : ''}
                >
                  Internal / Admin
                </option>
              </select>
            </label>

            <label
              data-aa-task-package-field
              ${selectedScope === 'client_deliverable' ? '' : 'hidden'}
            >
              Package Service
              <select
                data-aa-task-field="package_service_id"
              >
                ${this.getPackageServiceOptions(
                  selectedClientId,
                  selectedPackageService,
                  task?.due_date || ''
                )}
              </select>
            </label>

            <label
              data-aa-task-package-field
              ${selectedScope === 'client_deliverable' ? '' : 'hidden'}
            >
              Package Units
              <input
                type="number"
                min="0.01"
                step="0.01"
                data-aa-task-field="package_units"
                value="${this.escapeHtml(task?.package_units || 1)}"
              >
            </label>

            <label class="aa-task-editor__wide">
              Specific Item
              <input
                type="text"
                data-aa-task-field="specific_item"
                value="${this.escapeHtml(task?.specific_item || '')}"
                placeholder="What specifically needs to be done?"
                required
              >
            </label>

            <label>
              Assigned To
              <select data-aa-task-field="assignee_team_member_id">
                ${this.getAssigneeOptions(defaultAssignee)}
              </select>
            </label>

            <label>
              Status
              <select data-aa-task-field="status">
                ${this.getStatusOptions(
                  task?.status || 'Not Started'
                )}
              </select>
            </label>

            <label>
              Due Date
              <input
                type="date"
                data-aa-task-field="due_date"
                data-aa-date-picker
                value="${this.escapeHtml(task?.due_date || '')}"
              >
            </label>

            <div
              class="aa-task-editor__wide"
              data-aa-task-package-quota-wrap
            ></div>

            <label class="aa-task-editor__wide">
              Notes / Details
              <textarea
                data-aa-task-field="notes"
                rows="5"
              >${this.escapeHtml(task?.notes || '')}</textarea>
            </label>
          </div>

          <div class="aa-task-editor__actions">
            ${task ? `
              <button
                type="button"
                class="aa-button"
                data-aa-task-archive="${this.escapeHtml(task.id)}"
              >
                Archive
              </button>
            ` : '<span></span>'}

            <button
              type="submit"
              class="aa-button aa-button--primary"
            >
              ${task ? 'Save Changes' : 'Add Task'}
            </button>
          </div>
        </form>
      </div>
    `;
  }

  renderTaskTable(tasks) {
    return `
      <div class="aa-task-table">
        <div class="aa-task-row aa-task-row--header">
          <span>Client</span>
          <span>Task Type</span>
          <span>Specific Item</span>
          <span>Assigned To</span>
          <span>Status</span>
          <span>Due</span>
          <span>Notes</span>
          <span></span>
        </div>

        ${tasks.map(
          (task) => `
            <div
              class="aa-task-row"
              data-aa-task-row="${this.escapeHtml(task.id)}"
            >
              <span data-label="Client">
                ${this.escapeHtml(
                  this.getClientName(task.client_id)
                )}
              </span>

              <span
                data-label="Task Type"
                class="aa-task-type-cell"
              >
                <strong>
                  ${this.escapeHtml(task.task_type)}
                </strong>
              </span>

              <strong data-label="Specific Item">
                ${this.escapeHtml(task.specific_item)}
              </strong>

              <label data-label="Assigned To">
                <span class="aa-visually-hidden">
                  Assigned To
                </span>
                <select
                  data-aa-task-inline-assignee="${this.escapeHtml(task.id)}"
                >
                  ${this.getAssigneeOptions(
                    task.assignee_team_member_id
                  )}
                </select>
              </label>

              <label data-label="Status">
                <span class="aa-visually-hidden">
                  Status
                </span>
                <select
                  data-aa-task-inline-status="${this.escapeHtml(task.id)}"
                >
                  ${this.getStatusOptions(task.status)}
                </select>
              </label>

              <label data-label="Due">
                <span class="aa-visually-hidden">
                  Due Date
                </span>
                <input
                  type="date"
                  class="aa-task-inline-date"
                  data-aa-task-inline-due="${this.escapeHtml(task.id)}"
                  value="${this.escapeHtml(task.due_date || '')}"
                >
              </label>

              <span
                class="aa-task-notes"
                data-label="Notes"
                title="${this.escapeHtml(task.notes || '')}"
              >
                ${this.escapeHtml(task.notes || '—')}
              </span>

              <button
                type="button"
                class="aa-task-edit-button"
                data-aa-task-edit="${this.escapeHtml(task.id)}"
              >
                Edit
              </button>
            </div>
          `
        ).join('')}
      </div>
    `;
  }

  renderEmptyState() {
    const label =
      this.activeAssigneeFilter === 'all'
        ? 'No production tasks yet.'
        : `No current tasks for ${
            this.escapeHtml(
              this.getTeamMemberName(
                this.activeAssigneeFilter
              )
            )
          }.`;

    return `
      <div class="aa-card aa-headquarters__card aa-task-empty">
        <p class="aa-card__label">Work Queue</p>
        <h3>${label}</h3>
        <p>
          Add a task here now. Catalog bulk paste,
          recurring tasks, and client-submitted requests
          will feed this same list.
        </p>
      </div>
    `;
  }

  showFeedback(message) {
    const feedback =
      this.root.querySelector(
        '[data-aa-task-feedback]'
      );

    if (!feedback) {
      return;
    }

    feedback.textContent = message;
    feedback.hidden = false;

    window.clearTimeout(
      this.feedbackTimer
    );

    this.feedbackTimer =
      window.setTimeout(() => {
        feedback.hidden = true;
      }, 3000);
  }

  async refreshTasks() {
    const [
      tasks,
      packageServices,
      taskTypes,
      teamSummary
    ] = await Promise.all([
      window.AASupabaseClient
        .getTasks(),
      window.AASupabaseClient
        .getTaskPackageServices?.() ||
        Promise.resolve(
          this.packageServices
        ),
      window.AASupabaseClient
        .getTaskTypes?.() ||
        Promise.resolve(
          this.taskTypes
        ),
      window.AASupabaseClient
        .getTeamTaskSummary?.({
          month:
            this.getTeamPaceMonth()
        }) ||
        Promise.resolve(
          this.teamSummary
        )
    ]);

    this.tasks =
      Array.isArray(tasks)
        ? tasks
        : [];

    this.packageServices =
      Array.isArray(packageServices)
        ? packageServices
        : [];

    this.taskTypes =
      Array.isArray(taskTypes)
        ? taskTypes
        : [];

    this.teamSummary =
      Array.isArray(teamSummary)
        ? teamSummary
        : [];

    this.render();
  }

  bindEvents() {
    this.root.addEventListener(
      'click',
      async (event) => {
        const viewButton =
          event.target.closest(
            '[data-aa-task-view]'
          );

        if (viewButton) {
          this.activeView =
            viewButton.dataset
              .aaTaskView ||
            'active';

          this.filterStatus = 'all';
          this.render();
          return;
        }

        const dateField =
          event.target.closest(
            '[data-aa-date-picker]'
          );

        if (
          dateField &&
          typeof dateField.showPicker ===
            'function'
        ) {
          try {
            dateField.showPicker();
          } catch (_) {
            // Native date input remains usable.
          }
        }

        const bulkOpenButton =
          event.target.closest(
            '[data-aa-task-bulk-open]'
          );

        if (bulkOpenButton) {
          this.bulkPasteOpen = true;
          this.editorOpen = false;
          this.editingTaskId = null;
          this.render();
          return;
        }

        const bulkCloseButton =
          event.target.closest(
            '[data-aa-task-bulk-close]'
          );

        if (bulkCloseButton) {
          this.bulkPasteOpen = false;
          this.bulkPasteText = '';
          this.bulkRows = [];
          this.render();
          return;
        }

        const bulkReviewButton =
          event.target.closest(
            '[data-aa-task-bulk-review]'
          );

        if (bulkReviewButton) {
          const bulkSection =
            bulkReviewButton.closest(
              '.aa-task-bulk'
            );

          const textarea =
            bulkSection?.querySelector(
              '[data-aa-task-bulk-text]'
            );

          try {
            this.bulkPasteText =
              textarea?.value || '';

            this.bulkRows =
              this.parseBulkPaste(
                this.bulkPasteText
              );

            this.render();

            requestAnimationFrame(() => {
              this.root
                .querySelector(
                  '[data-aa-task-bulk-import]'
                )
                ?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start'
                });
            });
          } catch (error) {
            console.error(
              'Unable to review pasted tasks:',
              error
            );

            this.showFeedback(
              'We could not parse those pasted rows. Check the spreadsheet columns and try again.'
            );
          }

          return;
        }

        const bulkSelectAllButton =
          event.target.closest(
            '[data-aa-task-bulk-select-all]'
          );

        if (bulkSelectAllButton) {
          this.root
            .querySelectorAll(
              '[data-aa-bulk-field="include"]'
            )
            .forEach((checkbox) => {
              checkbox.checked = true;
            });

          return;
        }

        const bulkClearAllButton =
          event.target.closest(
            '[data-aa-task-bulk-clear-all]'
          );

        if (bulkClearAllButton) {
          this.root
            .querySelectorAll(
              '[data-aa-bulk-field="include"]'
            )
            .forEach((checkbox) => {
              checkbox.checked = false;
            });

          return;
        }

        const addButton =
          event.target.closest(
            '[data-aa-task-add]'
          );

        if (addButton) {
          this.editingTaskId = null;
          this.editorOpen = true;
          this.setTaskUrl(null);
          this.render();
          return;
        }

        const cancelButton =
          event.target.closest(
            '[data-aa-task-cancel]'
          );

        if (cancelButton) {
          this.editingTaskId = null;
          this.editorOpen = false;
          this.setTaskUrl(null);
          this.render();
          return;
        }

        const editorOverlay =
          event.target.closest(
            '[data-aa-task-editor-overlay]'
          );

        if (
          editorOverlay &&
          event.target === editorOverlay
        ) {
          this.editingTaskId = null;
          this.editorOpen = false;
          this.setTaskUrl(null);
          this.render();
          return;
        }

        const filterButton =
          event.target.closest(
            '[data-aa-task-filter]'
          );

        if (filterButton) {
          this.activeAssigneeFilter =
            filterButton.dataset.aaTaskFilter ||
            'all';

          this.render();
          return;
        }

        const editButton =
          event.target.closest(
            '[data-aa-task-edit]'
          );

        if (editButton) {
          this.editingTaskId =
            editButton.dataset.aaTaskEdit;

          this.editorOpen = true;
          this.setTaskUrl(
            this.editingTaskId
          );
          this.render();
          return;
        }

        const archiveButton =
          event.target.closest(
            '[data-aa-task-archive]'
          );

        if (archiveButton) {
          archiveButton.disabled = true;
          archiveButton.textContent =
            'Archiving...';

          try {
            await window.AASupabaseClient.archiveTask({
              id:
                archiveButton.dataset
                  .aaTaskArchive
            });

            this.editorOpen = false;
            this.editingTaskId = null;
            this.setTaskUrl(null);

            await this.refreshTasks();
            this.showFeedback(
              'Task archived.'
            );
          } catch (error) {
            console.error(
              'Unable to archive task:',
              error
            );

            archiveButton.disabled = false;
            archiveButton.textContent =
              'Archive';
          }

          return;
        }
      }
    );

    this.root.addEventListener(
      'change',
      async (event) => {
        const clientField =
          event.target.closest(
            '[data-aa-task-field="client_id"]'
          );

        if (clientField) {
          const form =
            clientField.closest(
              '[data-aa-task-form]'
            );

          const assigneeField =
            form?.querySelector(
              '[data-aa-task-field="assignee_team_member_id"]'
            );

          if (assigneeField) {
            assigneeField.value =
              this.clientAssignments[
                String(clientField.value)
              ] ||
              this.currentTeamMemberId ||
              '';
          }

          const packageField =
            form?.querySelector(
              '[data-aa-task-field="package_service_id"]'
            );

          if (packageField) {
            packageField.innerHTML =
              this.getPackageServiceOptions(
                clientField.value,
                '',
                form?.querySelector(
                  '[data-aa-task-field="due_date"]'
                )?.value || ''
              );
          }

          if (!this.editingTaskId) {
            const scopeField =
              form?.querySelector(
                '[data-aa-task-field="task_scope"]'
              );

            if (scopeField) {
              scopeField.value =
                clientField.value
                  ? 'client_deliverable'
                  : 'internal_admin';
            }
          }

          this.syncTaskEditor(
            form,
            { autoMap: true }
          );

          return;
        }

        const listFilter =
          event.target.closest(
            '[data-aa-task-list-filter]'
          );

        if (listFilter) {
          const name =
            listFilter.dataset
              .aaTaskListFilter;

          const value =
            listFilter.value || 'all';

          if (name === 'client') {
            this.filterClientId = value;
          }

          if (name === 'task_type') {
            this.filterTaskType = value;
          }

          if (name === 'assignee') {
            this.filterAssigneeId = value;
          }

          if (name === 'status') {
            this.filterStatus = value;
          }

          if (name === 'month') {
            this.filterMonth = value;

            this.refreshTeamSummary()
              .then(
                () => this.render()
              );

            return;
          }

          this.render();
          return;
        }

        const editorField =
          event.target.closest(
            '[data-aa-task-form] [data-aa-task-field]'
          );

        if (editorField) {
          const form =
            editorField.closest(
              '[data-aa-task-form]'
            );

          if (
            editorField.dataset
              .aaTaskField === 'task_type'
          ) {
            if (
              editorField.value ===
              '__add__'
            ) {
              const input =
                form?.querySelector(
                  '[data-aa-new-task-type]'
                );

              if (input) {
                input.hidden = false;
                input.focus();
              }

              return;
            }

            this.syncTaskEditor(
              form,
              { autoMap: true }
            );
            return;
          }

          this.syncTaskEditor(form);
          return;
        }

        const statusField =
          event.target.closest(
            '[data-aa-task-inline-status]'
          );

        if (statusField) {
          statusField.disabled = true;

          try {
            await window.AASupabaseClient.updateTask({
              id:
                statusField.dataset
                  .aaTaskInlineStatus,
              updates: {
                status: statusField.value
              }
            });

            await this.refreshTasks();
          } catch (error) {
            console.error(
              'Unable to update task status:',
              error
            );

            statusField.disabled = false;
          }

          return;
        }

        const dueField =
          event.target.closest(
            '[data-aa-task-inline-due]'
          );

        if (dueField) {
          dueField.disabled = true;

          try {
            await window.AASupabaseClient.updateTask({
              id:
                dueField.dataset
                  .aaTaskInlineDue,
              updates: {
                due_date:
                  dueField.value || null
              }
            });

            await this.refreshTasks();
          } catch (error) {
            console.error(
              'Unable to update task due date:',
              error
            );

            dueField.disabled = false;
          }

          return;
        }

        const assigneeField =
          event.target.closest(
            '[data-aa-task-inline-assignee]'
          );

        if (assigneeField) {
          assigneeField.disabled = true;

          try {
            await window.AASupabaseClient.updateTask({
              id:
                assigneeField.dataset
                  .aaTaskInlineAssignee,
              updates: {
                assignee_team_member_id:
                  assigneeField.value || null
              }
            });

            await this.refreshTasks();
          } catch (error) {
            console.error(
              'Unable to update task assignee:',
              error
            );

            assigneeField.disabled = false;
          }
        }
      }
    );

    this.root.addEventListener(
      'input',
      (event) => {
        const search =
          event.target.closest(
            '[data-aa-task-search]'
          );

        if (search) {
          this.taskSearch =
            search.value || '';

          const wrap =
            this.root.querySelector(
              '.aa-task-table-wrap'
            );

          const meta =
            this.root.querySelector(
              '.aa-task-results-meta span'
            );

          const tasks =
            this.getVisibleTasks();

          if (wrap) {
            wrap.innerHTML =
              tasks.length
                ? this.renderTaskTable(
                    tasks
                  )
                : this.renderEmptyState();
          }

          if (meta) {
            meta.textContent =
              `${tasks.length} result${tasks.length === 1 ? '' : 's'}`;
          }

          return;
        }

        const editorInput =
          event.target.closest(
            '[data-aa-task-form] [data-aa-task-field="package_units"]'
          );

        if (editorInput) {
          this.syncTaskEditor(
            editorInput.closest(
              '[data-aa-task-form]'
            )
          );
        }
      }
    );

    this.root.addEventListener(
      'focusout',
      async (event) => {
        const input =
          event.target.closest(
            '[data-aa-new-task-type]'
          );

        if (input) {
          await this.saveNewTaskType(
            input
          );
        }
      }
    );

    this.root.addEventListener(
      'keydown',
      async (event) => {
        const input =
          event.target.closest(
            '[data-aa-new-task-type]'
          );

        if (
          input &&
          event.key === 'Enter'
        ) {
          event.preventDefault();

          await this.saveNewTaskType(
            input
          );
        }
      }
    );

    this.root.addEventListener(
      'submit',
      async (event) => {
        const bulkParseForm =
          event.target.closest(
            '[data-aa-task-bulk-parse]'
          );

        if (bulkParseForm) {
          event.preventDefault();

          const textarea =
            bulkParseForm.querySelector(
              '[data-aa-task-bulk-text]'
            );

          this.bulkPasteText =
            textarea?.value || '';

          this.bulkRows =
            this.parseBulkPaste(
              this.bulkPasteText
            );

          this.render();
          return;
        }

        const bulkImportForm =
          event.target.closest(
            '[data-aa-task-bulk-import]'
          );

        if (bulkImportForm) {
          event.preventDefault();

          const rowElements = [
            ...bulkImportForm.querySelectorAll(
              '[data-aa-bulk-row]'
            )
          ];

          const selectedRows = [];

          rowElements.forEach((rowEl) => {
            const include =
              rowEl.querySelector(
                '[data-aa-bulk-field="include"]'
              )?.checked === true;

            if (!include) {
              return;
            }

            const getField = (name) =>
              rowEl.querySelector(
                `[data-aa-bulk-field="${name}"]`
              );

            selectedRows.push({
              clientId:
                getField('client_id')?.value ||
                null,
              taskType:
                getField('task_type')
                  ?.value.trim() || '',
              specificItem:
                getField('specific_item')
                  ?.value.trim() || '',
              assigneeTeamMemberId:
                getField(
                  'assignee_team_member_id'
                )?.value || null,
              status:
                getField('status')?.value ||
                'Not Started',
              dueDate:
                getField('due_date')?.value ||
                null,
              notes:
                getField('notes')
                  ?.value.trim() || null
            });
          });

          if (!selectedRows.length) {
            this.showFeedback(
              'Select at least one task to import.'
            );
            return;
          }

          const missingClient =
            selectedRows.find(
              (row) => !row.clientId
            );

          if (missingClient) {
            this.showFeedback(
              'One or more selected rows still need a client match. Choose the client in those rows, then import again.'
            );
            return;
          }

          const invalidRow =
            selectedRows.find(
              (row) =>
                !row.taskType ||
                !row.specificItem
            );

          if (invalidRow) {
            this.showFeedback(
              'Every selected row needs a Task Type and Specific Item.'
            );
            return;
          }

          const submitButton =
            bulkImportForm.querySelector(
              'button[type="submit"]'
            );

          if (submitButton) {
            submitButton.disabled = true;
            submitButton.textContent =
              'Adding Tasks...';
          }

          try {
            let addedCount = 0;

            for (const row of selectedRows) {
              const packageServiceId =
                this.resolvePackageServiceId(
                  row.clientId,
                  row.taskType,
                  row.dueDate || ''
                );

              await window.AASupabaseClient.createTask({
                ...row,
                origin: 'bulk_import',
                taskScope:
                  'client_deliverable',
                packageServiceId:
                  packageServiceId || null,
                countsTowardPackage:
                  Boolean(packageServiceId),
                packageUnits: 1
              });

              addedCount += 1;
            }

            this.bulkPasteOpen = false;
            this.bulkPasteText = '';
            this.bulkRows = [];

            await this.refreshTasks();

            this.showFeedback(
              `${addedCount} task${addedCount === 1 ? '' : 's'} added.`
            );
          } catch (error) {
            console.error(
              'Unable to import tasks:',
              error
            );

            if (submitButton) {
              submitButton.disabled = false;
              submitButton.textContent =
                'Add Selected Tasks';
            }
          }

          return;
        }

        const form =
          event.target.closest(
            '[data-aa-task-form]'
          );

        if (!form) {
          return;
        }

        event.preventDefault();

        const getField = (name) =>
          form.querySelector(
            `[data-aa-task-field="${name}"]`
          );

        const payload = {
          clientId:
            getField('client_id')?.value ||
            null,
          taskType:
            (
              getField('task_type')
                ?.value === '__add__'
                ? ''
                : getField('task_type')
                    ?.value.trim()
            ) || '',
          specificItem:
            getField('specific_item')
              ?.value.trim() || '',
          assigneeTeamMemberId:
            getField(
              'assignee_team_member_id'
            )?.value || null,
          status:
            getField('status')?.value ||
            'Not Started',
          dueDate:
            getField('due_date')?.value ||
            null,
          notes:
            getField('notes')
              ?.value.trim() || null,
          taskScope:
            getField('task_scope')
              ?.value ||
            (
              getField('client_id')?.value
                ? 'client_deliverable'
                : 'internal_admin'
            ),
          packageServiceId:
            getField('package_service_id')
              ?.value || null,
          packageUnits:
            Number(
              getField('package_units')
                ?.value || 1
            )
        };

        if (
          payload.taskScope !==
            'client_deliverable' ||
          !payload.packageServiceId
        ) {
          payload.packageServiceId = null;
          payload.countsTowardPackage =
            false;
        } else {
          payload.countsTowardPackage =
            true;
        }

        const submitButton =
          form.querySelector(
            'button[type="submit"]'
          );

        if (submitButton) {
          submitButton.disabled = true;
          submitButton.textContent =
            this.editingTaskId
              ? 'Saving...'
              : 'Adding...';
        }

        try {
          if (this.editingTaskId) {
            await window.AASupabaseClient.updateTask({
              id: this.editingTaskId,
              updates: payload
            });
          } else {
            await window.AASupabaseClient.createTask({
              ...payload,
              origin: 'manual'
            });
          }

          const wasEditing =
            Boolean(this.editingTaskId);

          this.editingTaskId = null;
          this.editorOpen = false;

          await this.refreshTasks();

          this.showFeedback(
            wasEditing
              ? 'Task saved.'
              : 'Task added.'
          );
        } catch (error) {
          console.error(
            'Unable to save task:',
            error
          );

          if (submitButton) {
            submitButton.disabled = false;
            submitButton.textContent =
              this.editingTaskId
                ? 'Save Changes'
                : 'Add Task';
          }
        }
      }
    );
  }
}

window.AATasksWorkspace =
  AATasksWorkspace;
